import { useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, CheckCircle2, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import {
  canUseEntitlementGate,
  billingService,
  compareBillingPlans,
  getBillingSubscriptionUiState,
  summarizeDowngradeOverages,
  useCancelSubscription,
  useChangeSubscriptionPlan,
  useConfirmSubscription,
  useRetryPayment,
  useStartSubscription,
  useTranslation,
  type BillingInterval,
  type BillingPlanCatalogItem,
  type BillingSummary,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { BillingDiagnosticsCard } from './billing-diagnostics-card';
import { BillingStatusBanner } from './billing-status-banner';
import { CancelSubscriptionDialog } from './cancel-subscription-dialog';
import { ChangePlanDialog } from './change-plan-dialog';
import { EntitlementUsageMeter } from './entitlement-usage-meter';
import { PayPalSubscriptionButton } from './paypal-subscription-button';
import { PlanCard } from './plan-card';
import {
  findPlan,
  formatBillingDate,
  formatBillingPrice,
  formatBytes,
  intervalPriceKey,
  overageLabelKey,
  planNameKey,
} from '../lib/billing-formatters';

function formatCountUsageValue(used: number, limit: number): string {
  return limit <= 0 ? `${limit}/${used}` : `${used}/${limit}`;
}

function formatStorageUsageValue(used: number, limit: number, locale: string): string {
  if (limit <= 0) return `${formatBytes(limit, locale)} / ${formatBytes(used, locale)}`;
  return `${formatBytes(used, locale)} / ${formatBytes(limit, locale)}`;
}

export function BillingPage({
  storeId,
  summary,
}: {
  readonly storeId: string;
  readonly summary: BillingSummary;
}): React.JSX.Element {
  const { t, lang } = useTranslation();
  const [interval, setInterval] = useState<BillingInterval>(
    summary.subscription?.interval ?? 'monthly',
  );
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanCatalogItem | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [approvalUrl, setApprovalUrl] = useState<string | null>(null);
  const [paypalPlan, setPaypalPlan] = useState<BillingPlanCatalogItem | null>(null);

  const startMutation = useStartSubscription(supabase);
  const confirmMutation = useConfirmSubscription(supabase);
  const cancelMutation = useCancelSubscription(supabase);
  const changeMutation = useChangeSubscriptionPlan(supabase);
  const retryMutation = useRetryPayment(supabase);

  const plans = useMemo(
    () =>
      summary.catalog
        .filter((plan) => plan.interval === interval)
        .sort((a, b) => compareBillingPlans(a.planKey, b.planKey)),
    [interval, summary.catalog],
  );

  const subscriptionUi = getBillingSubscriptionUiState(summary);
  const currentPlanKey = subscriptionUi.currentPlanKey;
  const currentInterval = subscriptionUi.currentInterval;
  const pendingApprovalPlanKey =
    summary.subscription?.state === 'approval_pending' && summary.subscription.interval === interval
      ? summary.subscription.planKey
      : null;
  const pendingApprovalInterval =
    summary.subscription?.state === 'approval_pending'
      ? (summary.subscription.interval ?? null)
      : null;
  const currentPlan = currentPlanKey
    ? findPlan(summary.catalog, currentPlanKey, summary.subscription?.interval ?? interval)
    : null;
  const paypalPlanName = paypalPlan ? t(planNameKey(paypalPlan.planKey)) : '';
  const paypalPlanPrice = paypalPlan
    ? t(intervalPriceKey(paypalPlan.interval), {
        price: formatBillingPrice(paypalPlan.amountAgorot, lang),
      })
    : '';
  const canUsePriority = canUseEntitlementGate(summary.entitlement, 'priority_support_indicator');
  const overages =
    selectedPlan && currentPlan && selectedPlan.planKey !== currentPlan.planKey
      ? summarizeDowngradeOverages(summary.entitlement, selectedPlan)
      : [];

  if (summary.accessMode === 'pilot') {
    return <PilotBillingPage summary={summary} />;
  }

  function handleError(error: unknown): void {
    toast.error(t(billingService.mapBillingError(error)));
  }

  function handleChoosePlan(plan: BillingPlanCatalogItem): void {
    setApprovalUrl(null);
    if (!currentPlan || summary.subscription?.state === 'approval_pending') {
      setPaypalPlan(plan);
      return;
    }
    setSelectedPlan(plan);
  }

  async function handleChangePlan(): Promise<void> {
    if (!selectedPlan || approvalUrl) return;
    try {
      const result = await changeMutation.mutateAsync({
        storeId,
        planKey: selectedPlan.planKey,
        interval: selectedPlan.interval,
      });
      setApprovalUrl(result.approvalUrl);
      toast.success(t('admin.billing.toast.reconsentReady'));
    } catch (error) {
      handleError(error);
    }
  }

  async function handleCancel(): Promise<void> {
    try {
      await cancelMutation.mutateAsync(storeId);
      setCancelOpen(false);
      toast.success(t('admin.billing.toast.cancelled'));
    } catch (error) {
      handleError(error);
    }
  }

  async function handleRetry(): Promise<void> {
    try {
      await retryMutation.mutateAsync(storeId);
      toast.success(t('admin.billing.toast.retryStarted'));
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="text-start">
          <h1 className="font-display text-sand-950 text-3xl">{t('admin.billing.title')}</h1>
          <p className="text-sand-600 mt-2 text-sm">{t('admin.billing.subtitle')}</p>
        </div>
        <div className="border-sand-200 bg-paper inline-flex rounded-full border p-1">
          {(['monthly', 'annual'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`rounded-full px-4 py-2 text-sm font-medium ${
                interval === value ? 'bg-zaatar text-white' : 'text-sand-600'
              }`}
              aria-pressed={interval === value}
              onClick={() => setInterval(value)}
            >
              {t(`admin.billing.interval.${value}`)}
            </button>
          ))}
        </div>
      </div>

      <BillingStatusBanner summary={summary} />

      {!summary.serverConfigured || !summary.paypalClientIdConfigured ? (
        <div
          role="alert"
          className="border-terracotta/40 bg-terracotta/10 rounded-card border p-4 text-start"
        >
          <p className="text-terracotta font-medium">
            {t('admin.billing.errors.configurationMissing')}
          </p>
        </div>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-3">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            currentPlanKey={currentPlanKey}
            currentInterval={currentInterval}
            pendingApprovalPlanKey={pendingApprovalPlanKey}
            pendingApprovalInterval={pendingApprovalInterval}
            disabled={
              !summary.isOwner || !summary.serverConfigured || !summary.paypalClientIdConfigured
            }
            isPending={startMutation.isPending || changeMutation.isPending}
            onChoose={() => handleChoosePlan(plan)}
          />
        ))}
      </section>

      {paypalPlan ? (
        <section className="rounded-card border-sand-200 bg-paper border p-5 text-start shadow-sm">
          <div className="border-sand-200 border-b pb-4">
            <p className="text-zaatar-deep text-xs font-semibold uppercase">
              {t('admin.billing.paypal.selectedLabel')}
            </p>
            <h2 className="font-display text-sand-900 mt-1 text-2xl">
              {t('admin.billing.paypal.selectedTitle', { plan: paypalPlanName })}
            </h2>
            <p className="text-sand-700 mt-2 text-sm font-medium">
              {t('admin.billing.paypal.selectedSummary', {
                plan: paypalPlanName,
                price: paypalPlanPrice,
                interpolation: { escapeValue: false },
              })}
            </p>
            <ul className="text-sand-600 mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <li>
                {t('admin.billing.plan.activeProducts', {
                  count: paypalPlan.activeProductLimit,
                })}
              </li>
              <li>
                {t('admin.billing.plan.mediaStorage', {
                  size: formatBytes(paypalPlan.mediaStorageBytes, lang),
                })}
              </li>
              <li>
                {t('admin.billing.plan.aiImageEdits', {
                  count: paypalPlan.aiImageEditsPerPeriod,
                })}
              </li>
            </ul>
            <p className="text-sand-600 mt-3 text-sm">{t('admin.billing.paypal.pendingNotice')}</p>
          </div>
          <p className="text-sand-800 mt-4 text-sm font-semibold">
            {t('admin.billing.paypal.paymentMethods')}
          </p>
          <PayPalSubscriptionButton
            storeId={storeId}
            plan={paypalPlan}
            disabled={!summary.isOwner}
            startSubscription={(input) => startMutation.mutateAsync(input)}
            confirmSubscription={(input) => confirmMutation.mutateAsync(input)}
            onPending={() => toast.message(t('admin.billing.currentPlan.pending'))}
            onApproved={() => {
              setPaypalPlan(null);
              toast.success(t('admin.billing.toast.activated'));
            }}
            onError={handleError}
          />
        </section>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-3">
        <EntitlementUsageMeter
          label={t('admin.billing.usage.activeProducts')}
          used={summary.entitlement.activeProductsUsed}
          limit={summary.entitlement.activeProductLimit}
          valueLabel={formatCountUsageValue(
            summary.entitlement.activeProductsUsed,
            summary.entitlement.activeProductLimit,
          )}
        />
        <EntitlementUsageMeter
          label={t('admin.billing.usage.mediaStorage')}
          used={summary.entitlement.mediaStorageBytesUsed}
          limit={summary.entitlement.mediaStorageBytes}
          valueLabel={formatStorageUsageValue(
            summary.entitlement.mediaStorageBytesUsed,
            summary.entitlement.mediaStorageBytes,
            lang,
          )}
        />
        <EntitlementUsageMeter
          label={t('admin.billing.usage.aiEdits')}
          used={summary.entitlement.aiImageEditsUsed}
          limit={summary.entitlement.aiImageEditsPerPeriod}
          valueLabel={formatCountUsageValue(
            summary.entitlement.aiImageEditsUsed,
            summary.entitlement.aiImageEditsPerPeriod,
          )}
        />
      </section>

      <section className="flex flex-wrap gap-3">
        {summary.entitlement.state === 'grace' || summary.entitlement.state === 'limited' ? (
          <Button
            type="button"
            onClick={() => void handleRetry()}
            disabled={retryMutation.isPending}
          >
            {t('admin.billing.actions.retryPayment')}
          </Button>
        ) : null}
        {subscriptionUi.canCancel ? (
          <Button type="button" variant="outline" onClick={() => setCancelOpen(true)}>
            {t('admin.billing.actions.cancel')}
          </Button>
        ) : null}
        {canUsePriority.allowed ? (
          <span className="bg-zaatar-wash text-zaatar-deep rounded-full px-3 py-2 text-sm">
            {t('admin.billing.plan.support.priority')}
          </span>
        ) : null}
      </section>

      <BillingDiagnosticsCard summary={summary} />

      <CancelSubscriptionDialog
        open={cancelOpen}
        paidAccessUntil={summary.entitlement.paidAccessUntil}
        isPending={cancelMutation.isPending}
        onOpenChange={setCancelOpen}
        onConfirm={() => void handleCancel()}
      />
      <ChangePlanDialog
        open={!!selectedPlan}
        plan={selectedPlan}
        overages={overages}
        approvalUrl={approvalUrl}
        isPending={changeMutation.isPending}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPlan(null);
            setApprovalUrl(null);
          }
        }}
        onConfirm={() => void handleChangePlan()}
      />
    </div>
  );
}

function PilotBillingPage({ summary }: { readonly summary: BillingSummary }): React.JSX.Element {
  const { t, lang } = useTranslation();
  const entitlement = summary.entitlement;
  const pilotEndsAt =
    formatBillingDate(
      entitlement.pilotAccessUntil ?? summary.pilotGrant?.accessEndsAt ?? null,
      lang,
    ) || t('admin.billing.diagnostics.empty');
  const isActive = entitlement.accessSource !== 'none' && entitlement.canUsePaidActions;
  const isPayPalBacked = entitlement.accessSource === 'paypal';
  const StatusIcon = isActive ? CheckCircle2 : AlertTriangle;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6">
      <div className="text-start">
        <h1 className="font-display text-sand-950 text-3xl">{t('admin.billing.title')}</h1>
        <p className="text-sand-600 mt-2 text-sm">{t('admin.billing.pilot.subtitle')}</p>
      </div>

      <section
        data-testid="billing-pilot-status"
        className={`rounded-card flex items-start gap-3 border p-5 text-start shadow-sm ${
          isActive
            ? 'border-zaatar-soft bg-zaatar-wash text-zaatar-deep'
            : 'border-terracotta/40 bg-terracotta/10 text-sand-800'
        }`}
      >
        <StatusIcon className="mt-0.5 size-5 shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="font-medium">
            {isActive
              ? t(
                  isPayPalBacked
                    ? 'admin.billing.pilot.paidActiveTitle'
                    : 'admin.billing.pilot.activeTitle',
                )
              : t('admin.billing.pilot.expiredTitle')}
          </p>
          <p className="mt-1 text-sm leading-6">
            {isActive
              ? t(
                  isPayPalBacked
                    ? 'admin.billing.pilot.paidActiveBody'
                    : 'admin.billing.pilot.activeBody',
                  { date: pilotEndsAt },
                )
              : t('admin.billing.pilot.expiredBody')}
          </p>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-card border-sand-200 bg-paper border p-5 text-start shadow-sm">
          <div className="flex items-center gap-3">
            <CalendarDays className="text-zaatar size-5" aria-hidden />
            <h2 className="font-display text-sand-900 text-xl">
              {t('admin.billing.pilot.windowTitle')}
            </h2>
          </div>
          <p className="text-sand-700 mt-3 text-sm">
            {t('admin.billing.pilot.windowBody', { date: pilotEndsAt })}
          </p>
        </div>

        <div className="rounded-card border-sand-200 bg-paper border p-5 text-start shadow-sm">
          <div className="flex items-center gap-3">
            <LayoutTemplate className="text-zaatar size-5" aria-hidden />
            <h2 className="font-display text-sand-900 text-xl">
              {t('admin.billing.pilot.storefrontTitle')}
            </h2>
          </div>
          <p className="text-sand-700 mt-3 text-sm">{t('admin.billing.pilot.storefrontBody')}</p>
          <Button asChild className="mt-4">
            <a href="/storefront/home/editor">{t('admin.billing.pilot.storefrontCta')}</a>
          </Button>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <EntitlementUsageMeter
          label={t('admin.billing.usage.activeProducts')}
          used={entitlement.activeProductsUsed}
          limit={entitlement.activeProductLimit}
          valueLabel={formatCountUsageValue(
            entitlement.activeProductsUsed,
            entitlement.activeProductLimit,
          )}
        />
        <EntitlementUsageMeter
          label={t('admin.billing.usage.mediaStorage')}
          used={entitlement.mediaStorageBytesUsed}
          limit={entitlement.mediaStorageBytes}
          valueLabel={formatStorageUsageValue(
            entitlement.mediaStorageBytesUsed,
            entitlement.mediaStorageBytes,
            lang,
          )}
        />
        <EntitlementUsageMeter
          label={t('admin.billing.usage.aiEdits')}
          used={entitlement.aiImageEditsUsed}
          limit={entitlement.aiImageEditsPerPeriod}
          valueLabel={formatCountUsageValue(
            entitlement.aiImageEditsUsed,
            entitlement.aiImageEditsPerPeriod,
          )}
        />
      </section>

      {entitlement.overages.length > 0 ? (
        <section
          role="alert"
          className="border-terracotta/40 bg-terracotta/10 rounded-card border p-4 text-start"
        >
          <p className="text-terracotta font-medium">{t('admin.billing.pilot.overageTitle')}</p>
          <ul className="text-sand-700 mt-2 space-y-1 text-sm">
            {entitlement.overages.map((overage) => (
              <li key={overage.gate}>{t(overageLabelKey(overage))}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
